/**
 * Build Your Own Sidequest — bundle builder
 * ------------------------------------------------------------------
 * Isolated to the build-a-bundle section. Touches no shared theme file,
 * no Shopify.actions, no Dawn cart-drawer, and never reloads the page.
 *
 * Threshold model: full-size UNITS count, not distinct products.
 *   tier 1  -> one mini free (shopper picks), the other may be bought
 *   tier 2  -> both minis free, any paid-mini choice is cleared
 *
 * Pricing shown here is an ESTIMATE of the merchant's own offer. Shopify's
 * automatic Buy X Get Y discounts are what actually zero the gift lines, and
 * the builder refuses to report success until it has seen them do so.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-sqb-root]';

  /* One runtime per page, even if the section script is evaluated again
     (theme editor re-render, duplicated section, app that re-injects). */
  var RT = window.__sqbRuntime;
  if (!RT) {
    RT = window.__sqbRuntime = { instances: new WeakMap(), booted: false };
  }

  /* ────────────────────────────────────────────────────────────────
   * Promotion adapter.
   *
   * Everything the UI knows about how the offer is *enforced* lives here.
   * Swapping Shopify automatic discounts for Appstle or a discount Function
   * means replacing this object and nothing else.
   * ──────────────────────────────────────────────────────────────── */
  var Promo = {
    id: 'shopify-automatic-bxgy',
    tierFor: function (units, cfg) {
      if (units >= cfg.tier2) return 2;
      if (units >= cfg.tier1) return 1;
      return 0;
    },
    giftAllowance: function (tier) {
      return tier >= 2 ? 2 : tier === 1 ? 1 : 0;
    },
    /** Cents the automatic discount is expected to remove. Preview only. */
    expectedRewardCents: function (giftKeys, catalogue) {
      return giftKeys.reduce(function (n, k) {
        return n + (catalogue[k] ? catalogue[k].price : 0);
      }, 0);
    }
  };

  /* ──────────────────────────── helpers ──────────────────────────── */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /** Locale-aware root, e.g. "/" or "/en-ee/". */
  function shopRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /**
   * Shopify money_format renderer.
   *   amount                                  1,134.65
   *   amount_no_decimals                      1,135
   *   amount_with_comma_separator             1.134,65
   *   amount_no_decimals_with_comma_separator 1.135
   * Output may contain HTML entities (&euro;), so it is never escaped.
   */
  function money(cents, format) {
    function group(value, decimals, thousands, decimal) {
      var fixed = Math.abs(value).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (value < 0 ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
    }
    var value = cents / 100;
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    var out;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': out = group(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = group(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = group(value, 0, '.', ','); break;
      default: out = group(value, 2, ',', '.');
    }
    return format ? format.replace(/\{\{\s*\w+\s*\}\}/, out) : out;
  }

  function focusables(el) {
    return [].slice.call(el.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    });
  }

  /* ═══════════════════════════ builder ═══════════════════════════ */

  function Builder(root) {
    this.root = root;

    var t1 = parseInt(root.dataset.tier1, 10);
    var t2 = parseInt(root.dataset.tier2, 10);
    this.cfg = {
      tier1: t1, tier2: t2,
      maxQty: parseInt(root.dataset.maxQty, 10) || 8,
      moneyFormat: root.dataset.moneyFormat || '{{amount}}',
      attr: (root.dataset.attr || 'sq_bundle').trim(),
      afterAdd: root.dataset.afterAdd || 'open',
      previewSoldOut: root.dataset.previewSoldOut === 'true'
    };
    // locale-aware endpoints; theme routes win, shop root fills the gaps
    var R = window.routes || {};
    this.urls = {
      add: (R.cart_add_url || shopRoot() + 'cart/add') + '.js',
      cart: (R.cart_url || shopRoot() + 'cart') + '.js',
      update: (R.cart_update_url || shopRoot() + 'cart/update') + '.js',
      change: (R.cart_change_url || shopRoot() + 'cart/change') + '.js',
      cartPage: R.cart_url || shopRoot() + 'cart'
    };

    this.full = [];
    this.mini = [];
    this.catalogue = {};
    this.readCatalogue();

    this.state = {
      qty: {},
      freeMini: null,     // remembered across tier 2 so a drop back can restore it
      paidMini: null,     // tier 1 only
      busy: false,
      error: '',
      locked: false,      // cleanup unconfirmed — resubmission is unsafe
      lastFocus: null
    };
    var self = this;
    this.full.forEach(function (p) { self.state.qty[p.key] = 0; });

    this.els = {
      miniGrid: root.querySelector('[data-sqb-grid="mini"]'),
      sumDesktop: root.querySelector('[data-sqb-summary="desktop"]'),
      sumMobile: root.querySelector('[data-sqb-summary="mobile"]'),
      bar: root.querySelector('[data-sqb-bar]'),
      gnav: root.querySelector('.sqb-gnav'),
      barMsg: root.querySelector('[data-sqb-bar-msg]'),
      barBtn: root.querySelector('[data-sqb-bar-btn]'),
      sheet: root.querySelector('[data-sqb-sheet]'),
      sheetPanel: root.querySelector('[data-sqb-sheet-panel]'),
      chips: [].slice.call(root.querySelectorAll('.sqb-chip'))
    };

    this.bind();
    this.render();
    this.emit('view', { products: this.full.length + this.mini.length });
  }

  Builder.prototype.readCatalogue = function () {
    var self = this;
    [].slice.call(this.root.querySelectorAll('[data-sqb-product]')).forEach(function (node) {
      var d = node.dataset;
      var p = {
        key: d.sqbProduct,
        role: d.role,
        vid: d.vid,
        title: d.title,
        handle: d.handle,
        price: parseInt(d.price, 10) || 0,
        available: d.available === 'true',
        thumb: d.thumb || ''
      };
      self.catalogue[p.key] = p;
      (p.role === 'mini' ? self.mini : self.full).push(p);
    });
  };

  /* ───────────────────── configuration guard ─────────────────────── */

  /** Merchant-readable problem, or '' when the offer is configured sanely. */
  Builder.prototype.configError = function () {
    var c = this.cfg, d = this.root.dataset;
    if (!(c.tier1 >= 1) || !(c.tier2 >= 1)) {
      return this.fill(d.errConfigRange || 'Bundle thresholds must be whole numbers of 1 or more.');
    }
    if (c.tier2 <= c.tier1) {
      return this.fill(d.errConfigOrder ||
        'Bundle misconfigured: the second threshold ([tier2]) must be higher than the first ([tier1]).');
    }
    if (!this.full.length || !this.mini.length) {
      return this.fill(d.errConfigProducts || 'Bundle misconfigured: assign at least one full-size deck and one mini deck.');
    }
    return '';
  };

  /** Substitutes [tier1] / [tier2] / [n] so copy survives editable thresholds. */
  Builder.prototype.fill = function (str, n) {
    return String(str == null ? '' : str)
      .replace(/\[tier1\]/g, this.cfg.tier1)
      .replace(/\[tier2\]/g, this.cfg.tier2)
      .replace(/\[n\]/g, n == null ? '' : n);
  };

  /* ─────────────────────────── derived state ─────────────────────── */

  Builder.prototype.inStock = function (p) {
    return p.available || this.cfg.previewSoldOut;
  };

  Builder.prototype.fullUnits = function () {
    var s = this.state;
    return this.full.reduce(function (n, p) { return n + (s.qty[p.key] || 0); }, 0);
  };

  Builder.prototype.tier = function () {
    if (this.configError()) return 0;
    return Promo.tierFor(this.fullUnits(), this.cfg);
  };

  Builder.prototype.availMinis = function () {
    var self = this;
    return this.mini.filter(function (p) { return self.inStock(p); });
  };

  /**
   * Free gifts, DERIVED on every read — never stored. That is what makes it
   * impossible to keep a gift after the decks that earned it are removed.
   */
  Builder.prototype.freeGifts = function () {
    var t = this.tier();
    if (t === 0) return [];
    var av = this.availMinis();
    if (Promo.giftAllowance(t) >= 2) return av.map(function (p) { return p.key; });
    var choice = this.state.freeMini;
    if (choice && av.some(function (p) { return p.key === choice; })) return [choice];
    return [];
  };

  /**
   * True when the available minis are not all the same price.
   *
   * Native Buy X Get Y decides for itself which eligible line to discount — it
   * has no idea about our `_sq_bundle_role`. With two minis in the cart at the
   * same price that is harmless: whichever line it picks, the shopper pays for
   * exactly one. With different prices it is not, because Shopify could
   * discount the cheaper one and the shopper would be overcharged. So the paid
   * second mini is only offered when the prices match.
   */
  Builder.prototype.minisPriceMismatch = function () {
    var av = this.availMinis();
    if (av.length < 2) return false;
    var first = av[0].price;
    return av.some(function (p) { return p.price !== first; });
  };

  /** Is the optional paid second mini offerable right now? */
  Builder.prototype.paidAllowed = function () {
    return this.tier() === 1 &&
           this.freeGifts().length === 1 &&
           this.availMinis().length > 1 &&
           !this.minisPriceMismatch();
  };

  /** The optional second mini, bought at full price. Tier 1 only. */
  Builder.prototype.paidMinis = function () {
    if (!this.paidAllowed()) return [];
    var key = this.state.paidMini;
    if (!key) return [];
    if (this.freeGifts().indexOf(key) > -1) return [];
    var p = this.catalogue[key];
    if (!p || !this.inStock(p)) return [];
    return [key];
  };

  Builder.prototype.giftsResolved = function () {
    var t = this.tier();
    if (t === 0) return false;
    if (Promo.giftAllowance(t) >= 2) return true;
    return this.freeGifts().length === 1 || this.availMinis().length === 0;
  };

  Builder.prototype.canSubmit = function () {
    return !this.state.busy && !this.state.locked && !this.configError() &&
           this.tier() >= 1 && this.giftsResolved();
  };

  /* ───────────────────────────── money ───────────────────────────── */

  Builder.prototype.deckCents = function () {
    var s = this.state;
    return this.full.reduce(function (n, p) { return n + (s.qty[p.key] || 0) * p.price; }, 0);
  };
  Builder.prototype.paidCents = function () {
    var self = this;
    return this.paidMinis().reduce(function (n, k) { return n + self.catalogue[k].price; }, 0);
  };
  Builder.prototype.rewardCents = function () {
    return Promo.expectedRewardCents(this.freeGifts(), this.catalogue);
  };
  Builder.prototype.regularCents = function () {
    return this.deckCents() + this.paidCents() + this.rewardCents();
  };
  Builder.prototype.estimatedTotalCents = function () {
    return this.regularCents() - this.rewardCents();
  };

  /* ──────────────────────────── messaging ────────────────────────── */

  Builder.prototype.progress = function () {
    var d = this.root.dataset;
    var units = this.fullUnits(), t = this.tier(), gifts = this.freeGifts();
    var remaining = this.cfg.tier1 - units;

    if (units === 0) return { msg: this.fill(d.msg0), cls: '' };
    if (t === 0) return { msg: this.fill(d.msg1, remaining), cls: '' };

    if (t === 1 && gifts.length === 0) {
      if (this.availMinis().length === 0) return { msg: this.fill(d.msgNoMini), cls: 'is-act' };
      return { msg: this.fill(d.msg2), cls: 'is-act' };
    }
    if (t === 1) return { msg: this.fill(d.msg2Done, this.cfg.tier2 - units), cls: 'is-on' };
    if (gifts.length < Promo.giftAllowance(t)) return { msg: this.fill(d.msg3Short), cls: 'is-on' };
    return { msg: this.fill(d.msg3), cls: 'is-on' };
  };

  /* ───────────────────────────── render ──────────────────────────── */

  Builder.prototype.render = function () {
    var self = this;
    [].slice.call(this.root.querySelectorAll('[data-sqb-product]')).forEach(function (node) {
      self.paintCard(node);
    });

    var html = this.summaryHTML();
    if (this.els.sumDesktop) this.els.sumDesktop.innerHTML = html;
    if (this.els.sumMobile) this.els.sumMobile.innerHTML = html;

    var count = this.fullUnits() + this.freeGifts().length + this.paidMinis().length;
    if (this.els.barBtn) this.els.barBtn.textContent = this.fill(this.root.dataset.barLabel, count);

    var pr = this.progress();
    if (this.els.barMsg) {
      this.els.barMsg.textContent = this.configError() || pr.msg;
      this.els.barMsg.className = 'sqb-bar__msg ' + pr.cls;
    }
    this.measureBar();
  };

  Builder.prototype.paintCard = function (node) {
    var d = this.root.dataset;
    var key = node.dataset.sqbProduct;
    var p = this.catalogue[key];
    var ok = this.inStock(p);
    var t = this.tier();

    if (p.role === 'full') {
      var q = this.state.qty[key] || 0;
      node.classList.toggle('is-selected', q > 0);
      node.classList.toggle('is-unavailable', !ok);
      var frozen = this.frozen();
      var addBtn = node.querySelector('[data-sqb-add]');
      var stepper = node.querySelector('[data-sqb-stepper]');
      if (addBtn) { addBtn.hidden = !ok || q > 0; addBtn.disabled = frozen; }
      if (stepper) {
        stepper.hidden = !ok || q === 0;
        var n = stepper.querySelector('[data-sqb-qty]');
        if (n) n.textContent = q;
        var inc = stepper.querySelector('[data-sqb-inc]');
        if (inc) inc.disabled = frozen || q >= this.cfg.maxQty;
        var dec = stepper.querySelector('[data-sqb-dec]');
        if (dec) dec.disabled = frozen;
      }
      return;
    }

    /* ---- mini card ---- */
    var isFree = this.freeGifts().indexOf(key) > -1;
    var isPaid = this.paidMinis().indexOf(key) > -1;
    var allowance = Promo.giftAllowance(t);
    var chosenSomething = this.freeGifts().length === 1;
    // at tier 1, the mini that is not the free one may be bought outright
    var canBuy = ok && !isFree && this.paidAllowed();

    node.classList.toggle('is-selected', isFree || isPaid);
    node.classList.toggle('is-gift', isFree);
    node.classList.toggle('is-unavailable', !ok);
    node.classList.toggle('is-locked', t === 0 && ok);

    var badgeFree = node.querySelector('[data-sqb-badge="free"]');
    var badgePaid = node.querySelector('[data-sqb-badge="paid"]');
    var badgeLock = node.querySelector('[data-sqb-badge="lock"]');
    if (badgeFree) badgeFree.hidden = !isFree;
    if (badgePaid) badgePaid.hidden = !isPaid;
    if (badgeLock) badgeLock.hidden = !(t === 0 && ok);

    var priceFree = node.querySelector('[data-sqb-price-free]');
    var priceNorm = node.querySelector('[data-sqb-price-normal]');
    if (priceFree) priceFree.hidden = !isFree;
    if (priceNorm) priceNorm.hidden = isFree;

    var soldout = node.querySelector('[data-sqb-soldout]');
    var lock = node.querySelector('[data-sqb-lockbtn]');
    var choose = node.querySelector('[data-sqb-choose]');
    var paid = node.querySelector('[data-sqb-paid]');
    var added = node.querySelector('[data-sqb-added]');

    if (soldout) soldout.hidden = ok;
    if (lock) lock.hidden = !(ok && t === 0);
    // tier 2: every available mini is a gift, nothing to choose or buy
    if (added) added.hidden = !(ok && allowance >= 2 && isFree);

    if (choose) {
      // always available at tier 1, so the choice can be changed or undone
      choose.hidden = !(ok && t === 1);
      choose.disabled = this.frozen();
      choose.setAttribute('aria-pressed', isFree ? 'true' : 'false');
      var cl = choose.querySelector('[data-sqb-choose-label]');
      if (cl) {
        cl.textContent = isFree ? d.chosenLabel
          : (chosenSomething ? (d.switchLabel || d.chooseLabel) : d.chooseLabel);
      }
      choose.classList.toggle('is-switch', !isFree && chosenSomething);
      var help = isFree ? (d.chosenHint || '') : '';
      if (help) choose.setAttribute('title', help); else choose.removeAttribute('title');
    }
    if (paid) {
      paid.hidden = !canBuy;
      paid.disabled = this.frozen();
      paid.setAttribute('aria-pressed', isPaid ? 'true' : 'false');
      var pl = paid.querySelector('[data-sqb-paid-label]');
      if (pl) {
        pl.innerHTML = isPaid
          ? esc(d.paidRemoveLabel || 'Remove')
          : esc(d.paidAddLabel || 'Add for [price]').replace('[price]', money(p.price, this.cfg.moneyFormat));
      }
    }

    var veil = node.querySelector('[data-sqb-veil]');
    if (veil) veil.hidden = !(ok && t === 0);
  };

  Builder.prototype.summaryHTML = function () {
    var self = this, d = this.root.dataset;
    var fmt = this.cfg.moneyFormat;
    var cfgErr = this.configError();
    var h = '';

    if (cfgErr) {
      h += '<p class="sqb-notice is-error" role="alert">' + esc(cfgErr) + '</p>';
      h += '<button class="sqb-cta" type="button" data-sqb-submit disabled>' +
           esc(this.fill(d.ctaBlocked)) + '</button>';
      return h;
    }

    var free = this.freeGifts(), paidKeys = this.paidMinis();
    var pr = this.progress();
    var pct = Math.min(100, (this.fullUnits() / this.cfg.tier2) * 100);

    h += '<div class="sqb-meter' + (pct >= 100 ? ' is-max' : '') + '" role="presentation">' +
         '<i style="width:' + pct + '%"></i></div>';
    h += '<p class="sqb-sum__progress ' + pr.cls + '" role="status">' + esc(pr.msg) + '</p>';

    var rows = [];
    this.full.forEach(function (p) {
      var q = self.state.qty[p.key] || 0;
      if (q > 0) {
        rows.push({
          k: p.key, title: p.title, qty: q, cents: p.price * q, kind: 'full',
          subHtml: money(p.price, fmt) + ' ' + esc(d.eachLabel || 'each')
        });
      }
    });
    free.forEach(function (k) {
      var p = self.catalogue[k];
      rows.push({ k: k, title: p.title, qty: 1, cents: p.price, kind: 'gift', subHtml: esc(d.giftLabel) });
    });
    paidKeys.forEach(function (k) {
      var p = self.catalogue[k];
      rows.push({ k: k, title: p.title, qty: 1, cents: p.price, kind: 'paid', subHtml: esc(d.paidSubLabel || 'Second mini') });
    });

    if (!rows.length) {
      h += '<p class="sqb-sum__empty">' + esc(d.emptyLabel) + '</p>';
      h += '<button class="sqb-cta" type="button" data-sqb-submit disabled>' +
           esc(this.fill(d.ctaBlocked)) + '</button>';
      return h + this.noticesHTML();
    }

    h += '<ul class="sqb-lines">';
    rows.forEach(function (r) {
      var p = self.catalogue[r.k];
      var isGift = r.kind === 'gift';
      h += '<li class="sqb-line' + (isGift ? ' sqb-line--gift' : '') +
           (r.kind === 'paid' ? ' sqb-line--paid' : '') + '">';
      h += '<span class="sqb-line__thumb">' +
           (p.thumb ? '<img src="' + esc(p.thumb) + '" alt="" loading="lazy" width="46" height="46">' : '') +
           '</span>';
      h += '<span class="sqb-line__name"><b>' + esc(r.title) + (r.qty > 1 ? ' &times;' + r.qty : '') +
           '</b><span>' + r.subHtml + '</span></span>';
      h += '<span class="sqb-line__price' + (isGift ? ' is-free' : '') + '">' +
           (isGift ? esc(d.freeLabel) : money(r.cents, fmt)) + '</span>';
      if (r.kind === 'full') {
        h += '<button class="sqb-line__remove" type="button" data-sqb-remove="' + esc(r.k) + '"' +
             (self.frozen() ? ' disabled' : '') + ' ' +
             'aria-label="' + esc((d.removeLabel || 'Remove [title]').replace('[title]', r.title)) + '">' +
             '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg></button>';
      } else if (r.kind === 'paid') {
        h += '<button class="sqb-line__remove" type="button" data-sqb-drop-paid="' + esc(r.k) + '"' +
             (self.frozen() ? ' disabled' : '') + ' ' +
             'aria-label="' + esc((d.removeLabel || 'Remove [title]').replace('[title]', r.title)) + '">' +
             '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg></button>';
      }
      h += '</li>';
    });
    h += '</ul>';

    /* Value breakdown. Everything below is an estimate of the merchant's own
       offer — Shopify's automatic discount is the authority, and the builder
       says so until it has verified the discount landed. */
    var reward = this.rewardCents();
    h += '<div class="sqb-totals">';
    h += '<div><span>' + esc(d.regularValueLabel) + '</span><span class="v">' +
         money(this.regularCents(), fmt) + '</span></div>';
    if (reward > 0) {
      h += '<div class="is-reward"><span>' + esc(d.rewardLabel) + '</span><span class="v">&minus;' +
           money(reward, fmt) + '</span></div>';
    }
    h += '<div class="sqb-totals__grand"><span>' + esc(d.estTotalLabel) + '</span><span class="v">' +
         money(this.estimatedTotalCents(), fmt) + '</span></div>';
    if (reward > 0 && d.totalsNote) {
      h += '<div class="sqb-totals__note">' + esc(d.totalsNote) + '</div>';
    }
    h += '</div>';

    var label = this.tier() === 0
      ? this.fill(d.ctaBlocked)
      : (!this.giftsResolved() ? d.ctaPickMini : d.cta);

    h += '<button class="sqb-cta" type="button" data-sqb-submit ' + (this.canSubmit() ? '' : 'disabled') +
         (this.state.busy ? ' aria-busy="true"' : '') + '>' +
         esc(this.state.busy ? d.ctaBusy : label) + '</button>';

    return h + this.noticesHTML();
  };

  Builder.prototype.noticesHTML = function () {
    var d = this.root.dataset, h = '';
    if (this.state.error) {
      h += '<p class="sqb-notice is-error" role="alert">' + esc(this.state.error) + '</p>';
    }
    if (this.state.locked) {
      h += '<p class="sqb-notice is-error"><a class="sqb-review" href="' + esc(this.urls.cartPage) + '">' +
           esc(d.reviewCartLabel || 'Review cart') + '</a></p>';
    }
    var allowance = Promo.giftAllowance(this.tier());
    if (allowance > 0 && this.availMinis().length < allowance) {
      h += '<p class="sqb-notice is-warn">' + esc(d.miniStockNotice) + '</p>';
    }
    // The paid second mini is withheld when the minis cost different amounts.
    // Say so, rather than letting the option quietly vanish while the copy
    // elsewhere still offers it.
    if (this.tier() === 1 && this.availMinis().length > 1 && this.minisPriceMismatch() && d.miniPriceNotice) {
      h += '<p class="sqb-notice is-warn">' + esc(d.miniPriceNotice) + '</p>';
    }
    if (d.fineprint) h += '<p class="sqb-fineprint">' + esc(d.fineprint) + '</p>';
    return h;
  };

  /*
   * Publishes the two sticky heights the stylesheet needs. The mobile bar and
   * the group nav are measured independently: returning early when the bar is
   * absent — as this did — left --sqb-gnav-h unset on desktop, which is what
   * let the sticky pill row cover the "Your bundle" heading.
   */
  Builder.prototype.measureBar = function () {
    if (this.els.bar) {
      this.root.style.setProperty('--sqb-bar-h', this.els.bar.offsetHeight + 'px');
    }

    if (this.els.gnav) {
      this.root.style.setProperty('--sqb-gnav-h', this.els.gnav.offsetHeight + 'px');
    }
  };

  /* ───────────────────────────── events ──────────────────────────── */

  Builder.prototype.setQty = function (key, value) {
    if (this.frozen()) return;   // a submission is in flight
    var prev = this.state.qty[key] || 0;
    var next = Math.max(0, Math.min(this.cfg.maxQty, value));
    if (next === prev) return;
    var prevTier = this.tier();

    this.state.qty[key] = next;
    this.applyTierRules();
    this.clearError();
    this.render();

    this.emit(next > prev ? 'add' : 'remove', { product: key, quantity: next, previous: prev });
    if (this.tier() !== prevTier) this.emit('tier', { tier: this.tier(), units: this.fullUnits() });
  };

  /**
   * Keeps the mini selections consistent with the tier.
   *  - below tier 1: everything cleared
   *  - tier 2: both minis are gifts, so any paid designation is dropped
   *  - tier 1: the remembered free choice is preserved when it is still
   *    available; otherwise it is cleared and the shopper must choose again
   */
  Builder.prototype.applyTierRules = function () {
    var t = this.tier(), self = this;
    if (t === 0) {
      this.state.freeMini = null;
      this.state.paidMini = null;
      return;
    }
    if (Promo.giftAllowance(t) >= 2) {
      this.state.paidMini = null;   // both are gifts now
      return;
    }
    var av = this.availMinis().map(function (p) { return p.key; });
    if (this.state.freeMini && av.indexOf(this.state.freeMini) === -1) this.state.freeMini = null;
    if (this.state.paidMini &&
        (av.indexOf(this.state.paidMini) === -1 || this.state.paidMini === this.state.freeMini)) {
      this.state.paidMini = null;
    }
  };

  /**
   * One control, three jobs, all reflected in aria-pressed:
   *   nothing chosen        -> choose this one
   *   this one is chosen    -> deselect it
   *   the other is chosen   -> switch the free designation to this one
   *
   * Switching swaps rather than discards: if the mini being made free was the
   * paid one, the previously free mini takes the paid slot, so the shopper
   * still has both minis and the total does not move.
   */
  Builder.prototype.chooseFree = function (key) {
    if (this.frozen()) return;
    if (this.tier() < 1) return;
    var previousFree = this.freeGifts()[0] || null;
    var wasPaid = this.state.paidMini;

    if (previousFree === key) {
      // deselect: without a free choice the "second mini" has no meaning
      this.state.freeMini = null;
      this.state.paidMini = null;
      this.emit('clear-gift', { product: key });
    } else {
      this.state.freeMini = key;
      if (wasPaid === key && previousFree) {
        this.state.paidMini = previousFree;      // predictable swap
        this.emit('swap-gift', { free: key, paid: previousFree });
      } else {
        if (this.state.paidMini === key) this.state.paidMini = null;
        this.emit('choose-gift', { product: key });
      }
    }
    this.clearError();
    this.render();
  };

  Builder.prototype.togglePaid = function (key) {
    if (this.frozen()) return;
    if (!this.paidAllowed()) return;
    if (this.freeGifts().indexOf(key) > -1) return;
    var on = this.state.paidMini === key;
    this.state.paidMini = on ? null : key;
    this.clearError();
    this.render();
    this.emit(on ? 'remove-paid-mini' : 'add-paid-mini', { product: key });
  };

  Builder.prototype.clearError = function () {
    // a locked builder stays locked: the cart may hold orphaned lines
    if (!this.state.locked) this.state.error = '';
  };

  Builder.prototype.bind = function () {
    var self = this;

    this.onClick = function (e) {
      var el = e.target.closest(
        '[data-sqb-add],[data-sqb-inc],[data-sqb-dec],[data-sqb-remove],[data-sqb-choose],' +
        '[data-sqb-paid],[data-sqb-drop-paid],[data-sqb-more],[data-sqb-submit],' +
        '[data-sqb-sheet-open],[data-sqb-sheet-close]'
      );
      if (!el || !self.root.contains(el)) return;

      if (el.hasAttribute('data-sqb-add'))       return self.setQty(el.dataset.sqbAdd, 1);
      if (el.hasAttribute('data-sqb-inc'))       return self.setQty(el.dataset.sqbInc, (self.state.qty[el.dataset.sqbInc] || 0) + 1);
      if (el.hasAttribute('data-sqb-dec'))       return self.setQty(el.dataset.sqbDec, (self.state.qty[el.dataset.sqbDec] || 0) - 1);
      if (el.hasAttribute('data-sqb-remove'))    return self.setQty(el.dataset.sqbRemove, 0);
      if (el.hasAttribute('data-sqb-choose'))    return self.chooseFree(el.dataset.sqbChoose);
      if (el.hasAttribute('data-sqb-paid'))      return self.togglePaid(el.dataset.sqbPaid);
      if (el.hasAttribute('data-sqb-drop-paid')) return self.togglePaid(el.dataset.sqbDropPaid);
      if (el.hasAttribute('data-sqb-more')) {
        var panel = document.getElementById(el.getAttribute('aria-controls'));
        var open = el.getAttribute('aria-expanded') === 'true';
        el.setAttribute('aria-expanded', String(!open));
        if (panel) panel.hidden = open;
        return;
      }
      if (el.hasAttribute('data-sqb-submit'))      return self.submit();
      if (el.hasAttribute('data-sqb-sheet-open'))  return self.openSheet();
      if (el.hasAttribute('data-sqb-sheet-close')) return self.closeSheet();
    };
    this.root.addEventListener('click', this.onClick);

    this.onKeydown = function (e) {
      if (!self.els.sheet || !self.els.sheet.classList.contains('is-open')) return;
      if (e.key === 'Escape') return self.closeSheet();
      if (e.key !== 'Tab') return;
      var f = focusables(self.els.sheetPanel);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', this.onKeydown);

    this.onResize = function () { self.measureBar(); };
    window.addEventListener('resize', this.onResize);

    this.bindGroupNav();
    this.bindSmoothScroll();
  };

  /**
   * Smooth scrolling for the hero button and the group chips only — no global
   * `html { scroll-behavior: smooth }`, so nothing else on the store changes.
   * scrollIntoView honours the sections' scroll-margin-top, which is what keeps
   * headings clear of the sticky header and the sticky chip row.
   */
  Builder.prototype.bindSmoothScroll = function () {
    var self = this;
    this.onAnchor = function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a || !self.root.contains(a)) return;
      var id = a.getAttribute('href').slice(1);
      var target = self.root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id));
      if (!target) return;
      e.preventDefault();
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      } catch (err) {
        target.scrollIntoView(true);   // older engines: no options object
      }
      if (self.markChip) self.markChip(id);
      self.emit('jump', { target: id });
    };
    this.root.addEventListener('click', this.onAnchor);
  };

  /** Keeps the Full-size / Free minis chips truthful on click and on scroll. */
  Builder.prototype.bindGroupNav = function () {
    var self = this, chips = this.els.chips;
    if (!chips.length) return;

    function mark(id) {
      chips.forEach(function (c) {
        c.classList.toggle('is-active', c.getAttribute('href') === '#' + id);
      });
    }
    this.markChip = mark;

    this.onChipClick = function (e) {
      var c = e.target.closest('.sqb-chip');
      if (c && chips.indexOf(c) > -1) mark(c.getAttribute('href').slice(1));
    };
    this.root.addEventListener('click', this.onChipClick);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (x) { if (x.isIntersecting) mark(x.target.id); });
      }, { rootMargin: '-45% 0px -50% 0px' });
      chips.forEach(function (c) {
        var el = self.root.querySelector(c.getAttribute('href'));
        if (el) self.io.observe(el);
      });
    }
  };

  Builder.prototype.destroy = function () {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    this.root.removeEventListener('click', this.onClick);
    if (this.onChipClick) this.root.removeEventListener('click', this.onChipClick);
    if (this.onAnchor) this.root.removeEventListener('click', this.onAnchor);
    if (this.io) { this.io.disconnect(); this.io = null; }
    // never leave the page scroll-locked behind a sheet that no longer exists
    if (this.els.sheet) {
      this.els.sheet.classList.remove('is-open');
      this.els.sheet.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('sqb-no-scroll');
  };

  /* ───────────────────────── mobile sheet ────────────────────────── */

  Builder.prototype.openSheet = function () {
    if (!this.els.sheet) return;
    this.state.lastFocus = document.activeElement;
    this.els.sheet.classList.add('is-open');
    this.els.sheet.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sqb-no-scroll');
    if (this.els.sheetPanel) this.els.sheetPanel.focus();
  };

  Builder.prototype.closeSheet = function () {
    if (!this.els.sheet) return;
    this.els.sheet.classList.remove('is-open');
    this.els.sheet.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sqb-no-scroll');
    if (this.state.lastFocus && document.contains(this.state.lastFocus)) this.state.lastFocus.focus();
  };

  /* ──────────────────────── cart integration ─────────────────────── */

  Builder.prototype.buildItems = function (group) {
    var self = this, tier = String(this.tier()), items = [];
    function props(role) {
      return {
        _sq_bundle: 'byo-v1',
        _sq_bundle_group: group,
        _sq_bundle_tier: tier,
        _sq_bundle_role: role
      };
    }
    this.full.forEach(function (p) {
      var q = self.state.qty[p.key] || 0;
      if (q > 0) items.push({ id: p.vid, quantity: q, properties: props('full') });
    });
    this.freeGifts().forEach(function (k) {
      items.push({ id: self.catalogue[k].vid, quantity: 1, properties: props('gift') });
    });
    this.paidMinis().forEach(function (k) {
      items.push({ id: self.catalogue[k].vid, quantity: 1, properties: props('paid-mini') });
    });
    return items;
  };

  Builder.prototype.setBusy = function (busy) {
    this.state.busy = busy;
    if (busy) this.root.setAttribute('aria-busy', 'true');
    else this.root.removeAttribute('aria-busy');
    this.render();
  };

  /** True while a submission is in flight — the bundle is frozen. */
  Builder.prototype.frozen = function () {
    return this.state.busy;
  };

  /**
   * An immutable record of exactly what was submitted.
   *
   * Verification, cart attribution and success handling all read from this,
   * never from live UI state. Without it a shopper who changed the bundle
   * during the round trip would have the NEW selection verified against the
   * OLD cart write — the reward would be recomputed from a tier that was
   * never submitted.
   */
  Builder.prototype.snapshot = function (group) {
    var self = this;
    var qty = {};
    this.full.forEach(function (p) { qty[p.key] = self.state.qty[p.key] || 0; });
    var prices = {};
    this.mini.forEach(function (p) { prices[p.key] = p.price; });

    var snap = {
      group: group,
      tier: this.tier(),
      items: this.buildItems(group),
      expectedRewardCents: this.rewardCents(),
      fullUnits: this.fullUnits(),
      fullQty: qty,
      freeKeys: this.freeGifts().slice(),
      paidKeys: this.paidMinis().slice(),
      miniPrices: prices,
      miniPricesEqual: !this.minisPriceMismatch()
    };
    // shallow-freeze the nested containers too, so nothing can mutate it later
    Object.freeze(snap.items);
    snap.items.forEach(function (i) { Object.freeze(i); Object.freeze(i.properties); });
    Object.freeze(snap.fullQty);
    Object.freeze(snap.freeKeys);
    Object.freeze(snap.paidKeys);
    Object.freeze(snap.miniPrices);
    return Object.freeze(snap);
  };

  Builder.prototype.submit = function () {
    if (!this.canSubmit()) return;
    var self = this, d = this.root.dataset;
    var group = 'byo-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    var snap = this.snapshot(group);
    if (!snap.items.length) return;

    this.state.error = '';
    this.setBusy(true);   // from here the bundle is frozen
    this.emit('submit', { tier: snap.tier, lines: snap.items.length, group: group });

    postJSON(this.urls.add, { items: snap.items })
      .then(function (res) {
        return res.json().then(function (body) { return { ok: res.ok, body: body }; });
      })
      .then(function (r) {
        if (!r.ok) {
          var msg = (r.body && (r.body.description || r.body.message)) || d.errAdd;
          // an add can fail after partially landing; never assume it did not
          return self.recover(snap, msg, 'add-failed');
        }
        return self.verify(snap).then(function (v) {
          if (v.ok) return self.succeed(snap);
          var msg = v.reason === 'missing-lines' ? d.errVerify
                  : v.reason === 'mini-price-mismatch' ? d.errMiniPrices
                  : d.errDiscount;
          return self.recover(snap, msg, v.reason);
        });
      })
      .catch(function (err) {
        var msg = (err && err.message) || d.errAdd;
        return self.recover(snap, msg, 'network');
      });
  };

  /**
   * A bundle only counts as added when every expected line is present AND
   * Shopify has actually zeroed the gift lines. A console warning is not a
   * success condition.
   */
  Builder.prototype.verify = function (snap) {
    var self = this, group = snap.group, expected = snap.items;
    return fetch(this.urls.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart-unreadable');
        return r.json();
      })
      .then(function (cart) {
        var mine = (cart.items || []).filter(function (li) {
          return li.properties && li.properties._sq_bundle_group === group;
        });

        // 1. every expected variant/quantity present, per role
        var got = {};
        mine.forEach(function (li) {
          var k = li.variant_id + '|' + (li.properties._sq_bundle_role || '');
          got[k] = (got[k] || 0) + li.quantity;
        });
        var missing = expected.some(function (it) {
          return (got[it.id + '|' + it.properties._sq_bundle_role] || 0) < it.quantity;
        });
        if (missing) return { ok: false, reason: 'missing-lines' };

        // 2. the reward, checked in aggregate across the grouped MINI lines.
        //
        // Native Buy X Get Y chooses which eligible line to discount on its
        // own — it has never heard of `_sq_bundle_role`. So requiring the line
        // we tagged `gift` to be the zeroed one is wrong: with two equally
        // priced minis Shopify may legitimately discount the other. What must
        // hold is the money, not which row carries it.
        var miniLines = mine.filter(function (li) {
          var role = li.properties._sq_bundle_role;
          return role === 'gift' || role === 'paid-mini';
        });

        if (miniLines.length) {
          // The ambiguity only exists when a PAID mini sits alongside a gift:
          // Shopify could discount the cheaper line and overcharge the shopper.
          // When every mini line is a gift (tier 2) the aggregate is exact
          // whichever line it picks, so unequal prices are harmless there.
          var giftLines = miniLines.filter(function (li) {
            return li.properties._sq_bundle_role === 'gift';
          });
          if (miniLines.length > giftLines.length && !snap.miniPricesEqual) {
            return { ok: false, reason: 'mini-price-mismatch' };
          }

          var expectedReward = snap.expectedRewardCents;
          var miniOriginal = miniLines.reduce(function (n, li) {
            return n + self.originalLinePrice(li);
          }, 0);
          var allocated = miniLines.reduce(function (n, li) {
            return n + self.lineDiscount(li);
          }, 0);
          var miniFinal = miniLines.reduce(function (n, li) {
            return n + self.finalLinePrice(li);
          }, 0);

          if (allocated !== expectedReward) return { ok: false, reason: 'gift-not-discounted' };
          if (miniFinal !== miniOriginal - expectedReward) {
            return { ok: false, reason: 'gift-not-discounted' };
          }
        }

        // 3. the decks are never part of the reward
        var deckDiscounted = mine.some(function (li) {
          return li.properties._sq_bundle_role === 'full' && self.lineDiscount(li) !== 0;
        });
        if (deckDiscounted) return { ok: false, reason: 'unexpected-deck-discount' };

        self.emit('verified', {
          group: group,
          rewardCents: mine.reduce(function (n, li) { return n + self.lineDiscount(li); }, 0)
        });
        return { ok: true, cart: cart };
      })
      .catch(function () { return { ok: false, reason: 'cart-unreadable' }; });
  };

  /**
   * Line-level only — cart.total_discount can carry unrelated order discounts.
   * Order matters: an undiscounted figure must never be returned before the
   * allocations have been looked at, or a missing discount reads as applied.
   */
  Builder.prototype.finalLinePrice = function (li) {
    if (typeof li.final_line_price === 'number') return li.final_line_price;
    var allocs = li.line_level_discount_allocations || li.discounts || [];
    if (allocs.length || typeof li.original_line_price === 'number') {
      var off = allocs.reduce(function (n, a) { return n + (a.amount || 0); }, 0);
      return this.originalLinePrice(li) - off;
    }
    return this.originalLinePrice(li);   // last resort: nothing to subtract
  };

  /** Discount actually allocated to one line, line-level only. */
  Builder.prototype.lineDiscount = function (li) {
    var allocs = li.line_level_discount_allocations || li.discounts || [];
    if (allocs.length) return allocs.reduce(function (n, a) { return n + (a.amount || 0); }, 0);
    return this.originalLinePrice(li) - this.finalLinePrice(li);
  };
  Builder.prototype.originalLinePrice = function (li) {
    return typeof li.original_line_price === 'number'
      ? li.original_line_price
      : (li.price || 0) * (li.quantity || 1);
  };

  Builder.prototype.succeed = function (snap) {
    var self = this;
    return this.tagCart(snap).then(function () {
      self.emit('added', { group: snap.group, tier: snap.tier });
      self.setBusy(false);
      self.closeSheet();
      try { self.handoff(); }
      catch (e) { if (window.console) console.error('[build-a-bundle] cart handoff failed', e); }
    });
  };

  /**
   * Something went wrong after we may have written to the cart. Remove every
   * line belonging to this group, then either allow a clean retry or, if the
   * cleanup cannot be confirmed, stop the shopper resubmitting so they cannot
   * end up with the bundle twice.
   */
  Builder.prototype.recover = function (snap, message, reason) {
    var self = this, d = this.root.dataset, group = snap.group;
    this.emit('error', { message: message, reason: reason, group: group });

    return this.rollback(group).then(function (cleaned) {
      self.setBusy(false);
      if (cleaned) {
        self.state.locked = false;
        self.state.error = message;
      } else {
        self.state.locked = true;   // resubmission disabled
        self.state.error = d.errCleanup;
        self.emit('cleanup-failed', { group: group });
      }
      self.render();
      // deliberately no UpCart, no success state
    });
  };

  /**
   * Removes every line carrying this bundle group.
   *
   * Shopify documents that line-item keys can change when discount
   * applications change, so a list of keys collected once can go stale
   * mid-cleanup. Instead this re-reads the cart before each removal and only
   * ever acts on a key it has just seen. Bounded so a server that keeps
   * returning the same line cannot spin forever.
   */
  Builder.prototype.rollback = function (group, maxSteps) {
    var self = this;
    var budget = maxSteps || 25;

    function readCart() {
      return fetch(self.urls.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    function mineIn(cart) {
      return (cart && cart.items ? cart.items : []).filter(function (li) {
        return li.properties && li.properties._sq_bundle_group === group;
      });
    }

    function step(remaining) {
      if (remaining <= 0) return Promise.resolve(false);   // budget exhausted
      return readCart().then(function (cart) {
        if (!cart) return false;                            // cannot confirm
        var lines = mineIn(cart);
        if (!lines.length) return true;                     // nothing left
        var key = lines[0].key;
        if (!key) return false;                             // nothing to act on
        return postJSON(self.urls.change, { id: key, quantity: 0 })
          .then(function () { return step(remaining - 1); })
          .catch(function () { return false; });
      });
    }

    return step(budget);
  };

  /** Order-level attribution for a Shopify Flow to tag on. */
  Builder.prototype.tagCart = function (snap) {
    if (!this.cfg.attr) return Promise.resolve();
    var attrs = {};
    attrs[this.cfg.attr] = 'true';
    attrs[this.cfg.attr + '_tier'] = String(snap.tier);   // submitted, not current
    attrs[this.cfg.attr + '_group'] = snap.group;
    return postJSON(this.urls.update, { attributes: attrs })
      .then(function () {}).catch(function () {});   // never block the handoff
  };

  /**
   * UpCart owns the drawer on this store. Dawn's <cart-drawer> is still in the
   * DOM (cart_type is 'drawer'), and opening it alongside UpCart locks
   * scrolling on iOS — UpCart renders in a shadow root, so the theme's
   * click-outside handler never receives the tap.
   */
  Builder.prototype.handoff = function () {
    if (this.cfg.afterAdd === 'stay') {
      this.emit('handoff', { target: 'stay' });
      this.refreshBubble();
      return;
    }
    if (typeof window.upcartOpenCart === 'function') {
      try { if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart(); } catch (e) {}
      try {
        window.upcartOpenCart();
        this.emit('handoff', { target: 'upcart' });
        this.refreshBubble();
        return;
      } catch (e) {}
    }
    this.emit('handoff', { target: 'cart' });
    window.location.assign(this.urls.cartPage);
  };

  Builder.prototype.refreshBubble = function () {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch(shopRoot() + '?sections=cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data['cart-icon-bubble']) return;
        var next = new DOMParser()
          .parseFromString(data['cart-icon-bubble'], 'text/html')
          .querySelector('#cart-icon-bubble');
        if (next) bubble.innerHTML = next.innerHTML;
      })
      .catch(function () {});
  };

  /* ──────────────────────────── analytics ────────────────────────── */

  Builder.prototype.emit = function (name, detail) {
    var base = { section: this.root.dataset.sectionId || '' };
    if (detail) for (var k in detail) if (Object.prototype.hasOwnProperty.call(detail, k)) base[k] = detail[k];
    document.dispatchEvent(new CustomEvent('sq:bundle:' + name, { detail: base }));
  };

  /* ──────────────────────────── bootstrap ────────────────────────── */

  function mount(scope) {
    var nodes = [].slice.call((scope || document).querySelectorAll(SELECTOR));
    if (scope && scope.matches && scope.matches(SELECTOR)) nodes.push(scope);
    nodes.forEach(function (root) {
      if (RT.instances.has(root)) return;
      try {
        RT.instances.set(root, new Builder(root));
      } catch (e) {
        if (window.console) console.error('[build-a-bundle] failed to start', e);
      }
    });
  }

  function unmount(scope) {
    var nodes = [].slice.call((scope || document).querySelectorAll(SELECTOR));
    if (scope && scope.matches && scope.matches(SELECTOR)) nodes.push(scope);
    nodes.forEach(function (root) {
      var inst = RT.instances.get(root);
      if (inst) { inst.destroy(); RT.instances.delete(root); }
    });
  }

  // Document-level listeners are registered exactly once per page, however
  // many times this file is evaluated or however many sections exist.
  if (!RT.booted) {
    RT.booted = true;
    document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
    document.addEventListener('shopify:section:unload', function (e) { unmount(e.target); });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { mount(document); }, { once: true });
    }
  }

  // Mount whatever is already in the DOM for this evaluation.
  if (document.readyState !== 'loading') mount(document);
})();